import React, { useState, useEffect } from 'react';
import { Plus, X, MapPin, Clock, MessageSquare } from 'lucide-react';
import axios from 'axios';
import { getAuthConfig } from '../utils/auth';
import {
  getDemoMessages,
  isDemoUser,
  getDemoStations,
  createDemoStation,
  deleteDemoStation,
  getDemoStationMessages,
  addDemoMessageToStation,
} from '../utils/demoSession';

export default function StationBuilder({ eventId, userEmail, onClose, user = null }) {
  const [stations, setStations] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedStation, setSelectedStation] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    instructions: '',
    initialMessage: '',
    scheduledDate: '',
    scheduledTime: '',
    location: ''
  });
  const [messages, setMessages] = useState([]);
  const [stationMessages, setStationMessages] = useState({});
  const [loading, setLoading] = useState(false);
  const demoMode = isDemoUser(user) || userEmail === 'demo@demo.demo';

  const API_BASE = '';

  useEffect(() => {
    loadStations();
    loadMessages();
  }, [eventId]);

  const loadStations = async () => {
    try {
      if (demoMode) {
        setStations(getDemoStations(user, eventId));
        return;
      }

      const response = await axios.get(`${API_BASE}/api/events/${eventId}/stations`);
      setStations(response.data.stations || []);
    } catch (error) {
      console.error('Failed to load stations:', error);
    }
  };

  const loadMessages = async () => {
    try {
      if (demoMode) {
        setMessages(getDemoMessages(user));
        return;
      }

      const response = await axios.get('/api/messages', getAuthConfig());
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const handleCreateStation = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (demoMode) {
        createDemoStation(user, { eventId, ...formData });
      } else {
        const response = await axios.post(
          `${API_BASE}/api/stations/create?email=${userEmail}`,
          { eventId, ...formData }
        );
        console.log('✅ Station created:', response.data);
      }
      setFormData({ title: '', description: '', instructions: '', initialMessage: '', scheduledDate: '', scheduledTime: '', location: '' });
      setShowForm(false);
      loadStations();
    } catch (error) {
      console.error('Failed to create station:', error);
      alert('Fehler beim Erstellen der Station');
    }
    setLoading(false);
  };

  const handleDeleteStation = async (stationId) => {
    if (!window.confirm('Station wirklich löschen?')) return;
    try {
      if (demoMode) {
        deleteDemoStation(user, stationId);
      } else {
        await axios.delete(`${API_BASE}/api/stations/${stationId}?email=${userEmail}`);
        console.log('✅ Station deleted');
      }
      loadStations();
    } catch (error) {
      console.error('Failed to delete station:', error);
    }
  };

  const handleAddMessageToStation = async (stationId, messageId) => {
    try {
      if (demoMode) {
        addDemoMessageToStation(user, stationId, messageId);
      } else {
        const response = await axios.post(
          `${API_BASE}/api/stations/${stationId}/add-message?email=${userEmail}`,
          { messageId }
        );
        console.log('✅ Message added to station:', response.data);
      }
      if (selectedStation && selectedStation.id === stationId) {
        loadStationMessages(stationId);
      }
      loadStations();
    } catch (error) {
      console.error('Failed to add message:', error);
      alert('Fehler beim Hinzufügen der Nachricht');
    }
  };

  const loadStationMessages = async (stationId) => {
    try {
      if (demoMode) {
        setStationMessages(prev => ({
          ...prev,
          [stationId]: getDemoStationMessages(user, stationId)
        }));
        return;
      }

      const response = await axios.get(`${API_BASE}/api/stations/${stationId}/messages`);
      setStationMessages(prev => ({
        ...prev,
        [stationId]: response.data.messages || []
      }));
    } catch (error) {
      console.error('Failed to load station messages:', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-slate-800 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-slate-800 border-b border-slate-700 p-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <MapPin className="w-6 h-6 text-purple-500" />
            Stationen verwalten
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Create Station Form */}
          {!showForm ? (
            <button
              onClick={() => setShowForm(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg font-semibold transition-all"
            >
              <Plus className="w-5 h-5" />
              Neue Station hinzufügen
            </button>
          ) : (
            <form onSubmit={handleCreateStation} className="bg-slate-700/50 border border-slate-600 rounded-lg p-6 space-y-4">
              <h3 className="font-bold text-lg mb-4">Neue Station</h3>
              
              <input
                type="text"
                placeholder="Station Titel"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full bg-slate-700 border border-slate-600 rounded px-4 py-2 text-white placeholder-slate-400"
                required
              />

              <textarea
                placeholder="Beschreibung"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full bg-slate-700 border border-slate-600 rounded px-4 py-2 text-white placeholder-slate-400"
              />

              <textarea
                placeholder="Anleitung (was soll der Empfänger machen?)"
                value={formData.instructions}
                onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                className="w-full bg-slate-700 border border-slate-600 rounded px-4 py-2 text-white placeholder-slate-400"
              />

              <textarea
                placeholder="Initiale Nachricht (was ihn erwartet)"
                value={formData.initialMessage}
                onChange={(e) => setFormData({ ...formData, initialMessage: e.target.value })}
                className="w-full bg-slate-700 border border-slate-600 rounded px-4 py-2 text-white placeholder-slate-400"
              />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-2">Datum</label>
                  <input
                    type="date"
                    value={formData.scheduledDate}
                    onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded px-4 py-2 text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2">Uhrzeit</label>
                  <input
                    type="time"
                    value={formData.scheduledTime}
                    onChange={(e) => setFormData({ ...formData, scheduledTime: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded px-4 py-2 text-white"
                    required
                  />
                </div>
              </div>

              <input
                type="text"
                placeholder="Ort (z.B. Café, Bahnhof, etc.)"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="w-full bg-slate-700 border border-slate-600 rounded px-4 py-2 text-white placeholder-slate-400"
              />

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded font-semibold transition-all disabled:opacity-50"
                >
                  {loading ? '⏳ Erstelle...' : '✓ Erstellen'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 px-4 py-2 bg-slate-600 hover:bg-slate-700 rounded font-semibold transition-all"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          )}

          {/* Stations List */}
          <div className="space-y-4">
            {stations.length === 0 ? (
              <p className="text-center text-slate-400 py-8">Keine Stationen vorhanden</p>
            ) : (
              stations.map((station, index) => (
                <div key={station.id} className="bg-slate-700/50 border border-slate-600 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-lg">{station.title}</h3>
                      <p className="text-slate-400 text-sm">{station.description}</p>
                    </div>
                    <button
                      onClick={() => handleDeleteStation(station.id)}
                      className="text-red-400 hover:text-red-300 transition-all"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-slate-300 mb-3">
                    <Clock className="w-4 h-4" />
                    <span>{station.scheduled_date} um {station.scheduled_time}</span>
                  </div>

                  {station.location && (
                    <div className="flex items-center gap-2 text-sm text-slate-300 mb-3">
                      <MapPin className="w-4 h-4" />
                      <span>{station.location}</span>
                    </div>
                  )}

                  {/* Add Message Button */}
                  <button
                    onClick={() => {
                      setSelectedStation(station);
                      loadStationMessages(station.id);
                    }}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded text-sm font-semibold transition-all"
                  >
                    <MessageSquare className="w-4 h-4" />
                    Nachrichten verwalten ({station.message_count || 0})
                  </button>

                  {/* Manage Messages for this Station */}
                  {selectedStation && selectedStation.id === station.id && (
                    <div className="mt-4 p-4 bg-slate-800/50 rounded border border-slate-600">
                      <h4 className="font-bold mb-3 text-sm">Nachrichten dieser Station:</h4>
                      
                      {/* List of messages in station */}
                      <div className="space-y-2 mb-4 max-h-40 overflow-y-auto">
                        {stationMessages[station.id]?.map((msg, idx) => (
                          <div key={msg.id} className="flex items-start gap-2 text-sm p-2 bg-slate-700/50 rounded">
                            <span className="font-bold text-purple-400 min-w-max">#{idx + 1}</span>
                            <span className="text-slate-300">{msg.recipient_name && `Für ${msg.recipient_name}: `}{msg.message_text?.substring(0, 50)}</span>
                          </div>
                        ))}
                      </div>

                      {/* Add existing message */}
                      <div className="space-y-2">
                        <label className="block text-sm font-semibold mb-2">Nachricht hinzufügen:</label>
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              handleAddMessageToStation(station.id, e.target.value);
                              e.target.value = '';
                            }
                          }}
                          className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-white text-sm"
                        >
                          <option value="">-- Nachricht auswählen --</option>
                          {messages.map((msg) => (
                            <option key={msg.id} value={msg.id}>
                              {msg.recipient_name && `${msg.recipient_name}: `}{msg.message_text?.substring(0, 40)}...
                            </option>
                          ))}
                        </select>
                      </div>

                      <button
                        onClick={() => setSelectedStation(null)}
                        className="w-full mt-3 px-3 py-2 bg-slate-600 hover:bg-slate-700 rounded text-sm font-semibold transition-all"
                      >
                        Fertig
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
